import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { Link, useNavigate } from 'react-router-dom';
import { addFormData, fetchAllDataAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { FaRegEye, FaFilter, FaRedo, FaShoppingCart } from 'react-icons/fa';
import { CiExport } from "react-icons/ci";
import CsvDownloader from 'react-csv-downloader';
import { numberFormat } from '../components/numberFormat';
import { MdCancel } from "react-icons/md";
import withAuth from '../utils/withAuth';
import moment from 'moment';

const SkeletonRow = () => (
    <tr>
        {[...Array(12)].map((_, i) => (
            <td key={i}>
                <div
                    style={{
                        height: '14px',
                        borderRadius: '6px',
                        background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite'
                    }}
                />
            </td>
        ))}
    </tr>
);

const AbandonedOrder = () => {
    const ref = useRef();
    const [abandoned, setAbandoned] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filtervalue, setFilterValue] = useState({});
    const [historyModal, setHistoryModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [interactionHistory, setInteractionHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const subagentid = localStorage.getItem('subagentdata');
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const recordPerPage = 100;
    const lastIndex = currentPage * recordPerPage;
    const firstIndex = lastIndex - recordPerPage;
    const records = abandoned.slice(firstIndex, lastIndex);
    const npage = Math.ceil(abandoned.length / recordPerPage);
    const numbers = [...Array(npage + 1).keys()].slice(1);

    useEffect(() => {
        if (subagentid) {
            getAbandonedOrders();
        } else {
            navigate("/");
        }
    }, [subagentid, navigate]);

    const getAbandonedOrders = () => {
        setIsLoading(true);
        fetchAllDataAPI('sub-agent-get-abandoned-orders')
            .then((res) => {
                if (res?.data?.status === 1) {
                    setAbandoned(res.data.abandoned || []);
                    setCurrentPage(1);
                } else {
                    setAbandoned([]);
                    toast.warning("No abandoned orders found");
                }
            })
            .catch(() => {
                setAbandoned([]);
                toast.error("Failed to load");
            })
            .finally(() => setIsLoading(false));
    };

    const handleFilter = (e) => {
        e.preventDefault();

        if (filtervalue && Object.keys(filtervalue).length > 0) {
            setIsLoading(true);
            addFormData('sub-agent-filter-abandoned-orders', filtervalue)
                .then((res) => {
                    if (res?.data?.status === 1) {
                        setAbandoned(res.data.filterdata || []);
                        setCurrentPage(1);
                    } else {
                        setAbandoned([]);
                    }
                })
                .catch(() => toast.error("Filter failed"))
                .finally(() => setIsLoading(false));
        } else {
            getAbandonedOrders();
        }
    };

    const resetOrder = () => {
        if (ref.current) ref.current.reset();
        setFilterValue({});
        getAbandonedOrders();
    };

    const handleInputs = (e) => {
        setFilterValue({ ...filtervalue, [e.target.name]: e.target.value });
    };

    const openHistoryModal = (order) => {
        setSelectedOrder(order);
        setInteractionHistory([]);
        setHistoryLoading(true);
        setHistoryModal(true);
        getInteractionHistory(order.order_id);
    };

    const closeHistoryModal = () => {
        setHistoryModal(false);
        setSelectedOrder(null);
        setInteractionHistory([]);
        setHistoryLoading(false);
    };

    const getInteractionHistory = (orderId) => {
        if (!orderId) return;

        setHistoryLoading(true);

        addFormData('sub-agent-abandoned-order-history', {
            abandoned_order_id: orderId
        })
            .then((res) => {
                if (res?.data?.status === 1) {
                    setInteractionHistory(res.data.history || []);
                } else {
                    setInteractionHistory([]);
                }
            })
            .catch(() => {
                setInteractionHistory([]);
                toast.error('Failed to load history');
            })
            .finally(() => setHistoryLoading(false));
    };

    const columns = [
        { id: 'shipingid', displayName: 'Cart ID' },
        { id: 'cust_name', displayName: 'Name' },
        { id: 'cust_phone', displayName: 'Mobile' },
        { id: 'subtotal_price', displayName: 'Total Price' },
        { id: 'created_date', displayName: 'Order Date' },
        { id: 'created_time', displayName: 'Order Time' },
        { id: 'state', displayName: 'State' },
        { id: 'city', displayName: 'City' },
        { id: 'pincode', displayName: 'Pincode' },
        { id: 'source_name', displayName: 'Source' },
        { id: 'last_interaction_type', displayName: 'Last Interaction' },
        { id: 'last_interaction_by_name', displayName: 'Interaction By' },
        { id: 'last_interaction_note', displayName: 'Interaction Note' },
        { id: 'last_interaction_at', displayName: 'Interaction At' },
    ];

    return (
        <div className="misDashboard">
            <Helmet><title>SATKARTAR:SUB AGENT::ABANDONED ORDERS</title></Helmet>

            <style>{`
                @keyframes shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .abandoned-page { animation: fadeInUp 0.4s ease; }

                .abandoned-hero {
                    background: linear-gradient(135deg, #c62828 0%, #b71c1c 60%, #7f0000 100%);
                    border-radius: 16px; padding: 20px 24px;
                    color: #fff; margin-bottom: 20px;
                    box-shadow: 0 8px 24px rgba(198,40,40,0.25);
                    display: flex; align-items: center;
                    justify-content: space-between; flex-wrap: wrap; gap: 12px;
                    position: relative; overflow: hidden;
                }
                .abandoned-hero::before {
                    content: ''; position: absolute; top: -30px; right: -30px;
                    width: 140px; height: 140px; border-radius: 50%;
                    background: rgba(255,255,255,0.05);
                }

                .filter-card {
                    background: #fff; border-radius: 16px; padding: 18px 20px;
                    margin-bottom: 18px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: none;
                }
                .filter-card label {
                    font-size: 11px; font-weight: 600; color: #888;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    margin-bottom: 5px; display: block;
                }
                .filter-card .form-control {
                    border-radius: 10px; border: 1.5px solid #eee;
                    font-size: 13px; padding: 8px 12px; transition: border-color 0.2s;
                }
                .filter-card .form-control:focus {
                    border-color: #c62828; box-shadow: 0 0 0 3px rgba(198,40,40,0.08);
                }

                .abandoned-table-card {
                    background: #fff; border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.06);
                    border: none; overflow: visible;
                    margin-bottom: 50px;
                }
                .abandoned-table-card .table-scroll {
                    overflow-x: auto; -webkit-overflow-scrolling: touch;
                    border-radius: 0 0 16px 16px;
                }
                .abandoned-table-card .table-scroll::-webkit-scrollbar { height: 5px; }
                .abandoned-table-card .table-scroll::-webkit-scrollbar-track { background: #f5f5f5; }
                .abandoned-table-card .table-scroll::-webkit-scrollbar-thumb { background: #c62828; border-radius: 3px; }

                .abandoned-table-card table { margin: 0; min-width: 1350px; }
                .abandoned-table-card thead th {
                    background: #fff5f5; font-size: 11px; font-weight: 700;
                    color: #c62828; text-transform: uppercase; letter-spacing: 0.5px;
                    padding: 13px 12px; border-bottom: 2px solid #ffebee;
                    white-space: nowrap; position: sticky; top: 0; z-index: 2;
                }
                .abandoned-table-card tbody tr { transition: background 0.15s; border-bottom: 1px solid #fafafa; }
                .abandoned-table-card tbody tr:hover { background: #fff5f5; }
                .abandoned-table-card tbody td {
                    font-size: 13px; color: #444; padding: 11px 12px;
                    vertical-align: middle; white-space: nowrap;
                }
                .abandoned-table-card tfoot th {
                    background: #fff5f5; font-size: 11px; font-weight: 700;
                    color: #c62828; padding: 11px 12px;
                    text-transform: uppercase; letter-spacing: 0.5px;
                }

                .summary-bar {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 12px 20px; border-bottom: 1px solid #fff0f0; background: #fff8f8;
                    border-top-right-radius: 16px;border-top-left-radius: 16px;
                }
                .summary-bar span { font-size: 13px; color: #666; font-weight: 500; }
                .summary-bar strong { color: #c62828; }

                .cart-id {
                    font-size: 11px; font-weight: 700; color: #c62828;
                    font-family: monospace; max-width: 140px;
                    overflow: hidden; text-overflow: ellipsis; display: inline-block;
                }

                .action-btn {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 30px; height: 30px; border-radius: 8px; margin: 0 2px;
                    transition: all 0.2s; text-decoration: none;cursor: pointer;
                }
                .action-btn:hover { transform: translateY(-2px); }
                .action-btn.view { background: #e8f5e9; color: #0e7c52; }

                .round-pagination .page-item.actives .page-link {
                    background-color: #c62828 !important; border-color: #c62828 !important;
                    color: #fff !important; border-radius: 8px !important;
                }
                .round-pagination .page-item .page-link {
                    background-color: #fff; border: 1.5px solid #eee; color: #555;
                    border-radius: 8px !important; margin: 0 2px; font-size: 12px;
                    font-weight: 600; padding: 5px 12px; transition: all 0.2s;
                    box-shadow: none !important;
                }
                .round-pagination .page-item .page-link:hover {
                    background-color: #c62828 !important; border-color: #c62828 !important;
                    color: #fff !important;
                }
                .round-pagination .page-item .page-link:focus { box-shadow: none !important; outline: none; }

                .ab-last-cell {
                    min-width: 260px;
                    max-width: 320px;
                    white-space: normal !important;
                    cursor: pointer;
                }
                .ab-last-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 10px 12px;
                }

                .ab-history-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15,23,42,.6);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                }

                .ab-history-modal {
                    width: 100%;
                    max-width: 760px;
                    background: #fff;
                    border-radius: 18px;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0,0,0,.25);
                }

                .ab-history-header {
                    padding: 16px 18px;
                    background: linear-gradient(135deg,#0f172a 0%,#1e293b 100%);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justifyContent: space-between;
                }

                .ab-history-close {
                    background: rgba(255,255,255,.1);
                    color: #fff;
                    border: none;
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    cursor: pointer;
                    font-size: 18px;
                }

                .ab-history-body {
                    padding: 18px;
                    max-height: 500px;
                    overflow: auto;
                    background: #fffdf5;
                }

                .ab-history-item {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px;
                    margin-bottom: 10px;
                }
            `}</style>

            <ContentWrapper>
                <div className="abandoned-page">

                    <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
                                <li className="breadcrumb-item">Abandoned Orders</li>
                            </ol>
                        </nav>
                    </div>

                    <div className="abandoned-hero">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                background: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '22px'
                            }}>
                                <FaShoppingCart />
                            </div>
                            <div>
                                <div style={{ fontSize: '11px', opacity: 0.75, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Total Abandoned
                                </div>
                                <div style={{ fontSize: '26px', fontWeight: 800 }}>{abandoned.length} Orders</div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', opacity: 0.7 }}>Total Value</div>
                                <div style={{ fontSize: '18px', fontWeight: 700 }}>
                                    ₹{abandoned.reduce((s, i) => s + parseFloat(i.subtotal_price || 0), 0).toLocaleString()}
                                </div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', opacity: 0.7 }}>This Page</div>
                                <div style={{ fontSize: '18px', fontWeight: 700 }}>{records.length}</div>
                            </div>
                        </div>
                    </div>

                    <div className="filter-card">
                        <form onSubmit={handleFilter} ref={ref}>
                            <div className="row g-2 align-items-end">
                                <div className="col-12 col-lg-3">
                                    <label>Search By Name</label>
                                    <input
                                        name="cname"
                                        className="form-control"
                                        type="text"
                                        placeholder="Customer name"
                                        onChange={handleInputs}
                                    />
                                </div>

                                <div className="col-12 col-lg-3">
                                    <label>Cart / Checkout ID</label>
                                    <input
                                        name="cartid"
                                        className="form-control"
                                        type="text"
                                        placeholder="Cart ID"
                                        onChange={handleInputs}
                                    />
                                </div>

                                <div className="col-12 col-lg-2">
                                    <label>Mobile Number</label>
                                    <input
                                        name="cmobile"
                                        className="form-control"
                                        type="text"
                                        placeholder="Mobile number"
                                        onChange={handleInputs}
                                    />
                                </div>

                                <div className="col-12 col-lg-2">
                                    <label>Order Date</label>
                                    <input
                                        name="odate"
                                        className="form-control"
                                        type="date"
                                        onChange={handleInputs}
                                    />
                                </div>

                                <div className="col-12 col-lg-2">
                                    <div className="d-flex gap-2 align-items-center" style={{ paddingTop: '22px' }}>
                                        <button
                                            type="submit"
                                            className="btn btn-sm"
                                            style={{
                                                background: '#c62828',
                                                color: '#fff',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                padding: '7px 14px',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <FaFilter style={{ marginRight: '4px', fontSize: '10px' }} /> Filter
                                        </button>

                                        <button
                                            type="button"
                                            onClick={resetOrder}
                                            className="btn btn-sm"
                                            style={{
                                                background: '#fff3cd',
                                                color: '#856404',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                padding: '7px 10px'
                                            }}
                                        >
                                            <FaRedo style={{ fontSize: '11px' }} />
                                        </button>

                                        <CsvDownloader filename="Abandoned Orders" extension=".csv" datas={abandoned} columns={columns}>
                                            <button
                                                type="button"
                                                className="btn btn-sm"
                                                style={{
                                                    background: '#e3f2fd',
                                                    color: '#1565c0',
                                                    borderRadius: '8px',
                                                    fontWeight: 600,
                                                    fontSize: '12px',
                                                    padding: '7px 10px'
                                                }}
                                            >
                                                <CiExport style={{ fontSize: '14px' }} />
                                            </button>
                                        </CsvDownloader>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="abandoned-table-card">
                        <div className="summary-bar">
                            <span>Showing <strong>{records.length}</strong> of <strong>{abandoned.length}</strong> abandoned orders</span>
                            <span>Page <strong>{currentPage}</strong> of <strong>{npage || 1}</strong></span>
                        </div>

                        <div className="table-scroll">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Cart ID</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Price</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Source</th>
                                        <th>Last Interaction</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading
                                        ? [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                        : records.length === 0
                                            ? (
                                                <tr>
                                                    <td colSpan={12} className="text-center py-5">
                                                        <MdCancel style={{ fontSize: '32px', color: '#eee', display: 'block', margin: '0 auto 8px' }} />
                                                        <div style={{ color: '#bbb', fontSize: '14px' }}>No abandoned orders found</div>
                                                    </td>
                                                </tr>
                                            )
                                            : records.map((record, index) => (
                                                <tr key={record.order_id || index}>
                                                    <td style={{ color: '#aaa', fontSize: '12px' }}>
                                                        {firstIndex + index + 1}
                                                    </td>

                                                    <td>
                                                        <span className="cart-id" title={record?.shipingid}>
                                                            {record?.shipingid || '—'}
                                                        </span>
                                                    </td>

                                                    <td style={{ fontWeight: 500 }}>
                                                        {record?.cust_name}
                                                    </td>

                                                    <td>{record?.cust_phone}</td>

                                                    <td style={{ fontWeight: 600, color: '#c62828' }}>
                                                        {numberFormat(record?.subtotal_price)}
                                                    </td>

                                                    <td>{record?.created_date}</td>
                                                    <td style={{ color: '#888' }}>{record?.created_time}</td>
                                                    <td>{record?.state}</td>
                                                    <td>{record?.city}</td>

                                                    <td>
                                                        <span style={{
                                                            background: '#e3f2fd',
                                                            color: '#1565c0',
                                                            padding: '2px 8px',
                                                            borderRadius: '20px',
                                                            fontSize: '11px',
                                                            fontWeight: 600
                                                        }}>
                                                            {record?.source_name || 'fastrr'}
                                                        </span>
                                                    </td>

                                                    <td
                                                        className="ab-last-cell"
                                                        onClick={() => openHistoryModal(record)}
                                                    >
                                                        {record?.last_interaction_type ? (
                                                            <div className="ab-last-card">
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    marginBottom: 6,
                                                                    gap: 8
                                                                }}>
                                                                    <div style={{ fontWeight: 700 }}>
                                                                        {record?.last_interaction_type}
                                                                    </div>
                                                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                                                        {record?.last_interaction_by_name || '—'}({record?.last_int_emp_id || '—'})
                                                                    </div>
                                                                </div>

                                                                <div style={{ fontSize: 10 }}>
                                                                    {record?.last_interaction_note || '—'}
                                                                </div>

                                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                                                                    {record?.last_interaction_at
                                                                        ? moment(record.last_interaction_at, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YYYY hh:mm A')
                                                                        : '—'}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{ color: '#94a3b8' }}>No interaction yet</div>
                                                        )}
                                                    </td>

                                                    <td>
                                                        <Link
                                                            to={`/order-detail/${record?.order_id}`}
                                                            className="action-btn view"
                                                        >
                                                            <FaRegEye style={{ fontSize: '14px' }} />
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th>#</th>
                                        <th>Cart ID</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Price</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>State</th>
                                        <th>City</th>
                                        <th>Source</th>
                                        <th>Last Interaction</th>
                                        <th>Action</th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end p-3">
                            <nav>
                                <ul className="pagination round-pagination mb-0">
                                    <li className="page-item">
                                        <a
                                            href="#"
                                            className="page-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage !== 1) setCurrentPage(currentPage - 1);
                                            }}
                                        >
                                            ← Prev
                                        </a>
                                    </li>

                                    {numbers.slice(Math.max(0, currentPage - 2), Math.max(5, currentPage + 3)).map((n, i) => (
                                        <li key={i} className={`page-item ${currentPage === n ? 'actives' : ''}`}>
                                            <a
                                                href="#"
                                                className="page-link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCurrentPage(n);
                                                }}
                                            >
                                                {n}
                                            </a>
                                        </li>
                                    ))}

                                    <li className="page-item">
                                        <a
                                            href="#"
                                            className="page-link"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (currentPage !== npage) setCurrentPage(currentPage + 1);
                                            }}
                                        >
                                            Next →
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>

                    {historyModal && (
                        <div className="ab-history-overlay">
                            <div className="ab-history-modal">
                                <div className="ab-history-header">
                                    <div>
                                        Interaction History — {selectedOrder?.cust_name || selectedOrder?.cust_phone}
                                    </div>
                                    <button className="ab-history-close" onClick={closeHistoryModal}>×</button>
                                </div>

                                <div className="ab-history-body">
                                    {historyLoading ? (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>
                                            Loading...
                                        </div>
                                    ) : interactionHistory.length > 0 ? (
                                        interactionHistory.map((item) => (
                                            <div key={item.id} className="ab-history-item">
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: 8,
                                                    marginBottom: 6
                                                }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                                        {item.interaction_type}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                                        {item.agent_name || '—'}({item.agent_emp_id || '—'})
                                                    </div>
                                                </div>

                                                <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.45 }}>
                                                    {item.interaction_note || '—'}
                                                </div>

                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                                                    {item.created_at
                                                        ? moment(item.created_at, 'YYYY-MM-DD HH:mm:ss').format('DD MMM YYYY hh:mm A')
                                                        : '—'}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ color: '#94a3b8', textAlign: 'center', padding: 16 }}>
                                            No interaction yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(AbandonedOrder);